import React, { useState } from 'react';
import { CalculationResult, PayFrequency } from '../types/calculator';
import { Wallet, FileText, Percent, Check } from 'lucide-react';
import { AdSlot } from './AdSlot';

interface ResultsSummaryProps {
  result: CalculationResult;
}

export const ResultsSummary: React.FC<ResultsSummaryProps> = ({ result }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<PayFrequency>('month');
  const [copied, setCopied] = useState(false);

  const formatGBP = (val: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val || 0);
  };

  const getPeriodData = () => {
    switch (selectedPeriod) {
      case 'year':
        return { label: 'Yearly Take-Home Pay', data: result.breakdowns.yearly };
      case 'month':
        return { label: 'Monthly Take-Home Pay', data: result.breakdowns.monthly };
      case '4week':
        return { label: '4-Weekly Take-Home Pay', data: result.breakdowns.fourWeekly };
      case '2week':
        return { label: 'Fortnightly Take-Home Pay', data: result.breakdowns.fortnightly };
      case 'week':
        return { label: 'Weekly Take-Home Pay', data: result.breakdowns.weekly };
      case 'day':
        return { label: 'Daily Take-Home Pay', data: result.breakdowns.daily };
      case 'hour':
        return { label: 'Hourly Take-Home Pay', data: result.breakdowns.hourly };
    }
  };

  const activePeriod = getPeriodData();

  const handleCopySummary = () => {
    const text = `UK Salary Breakdown (${result.inputs.taxYear}):
Gross Salary: ${formatGBP(result.annualGross)} / yr
Monthly Take-Home Pay: ${formatGBP(result.breakdowns.monthly.netPay)}
Annual Income Tax: ${formatGBP(result.incomeTax)}
Annual National Insurance: ${formatGBP(result.nationalInsurance)}
Effective Tax Rate: ${result.effectiveTaxRate.toFixed(1)}%
Calculated with UK Take Home Pay Calculators`;

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-5">
      {/* Hero Take-Home Card */}
      <div className="bg-zinc-900 text-white rounded-2xl p-6 shadow-2xl relative overflow-hidden border border-zinc-800">
        {/* Subtle Background Glow */}
        <div className="absolute top-0 right-0 -mt-8 -mr-8 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 space-y-5">
          {/* Frequency Period Switcher Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-800 pb-4">
            <span className="text-xs font-bold uppercase tracking-wider text-zinc-400 flex items-center gap-1.5">
              <Wallet className="w-4 h-4 text-emerald-400" />
              Net Salary Result
            </span>

            <div className="flex items-center gap-1 bg-zinc-950 p-1 rounded-xl border border-zinc-800 overflow-x-auto max-w-full">
              {(['year', 'month', '4week', '2week', 'week', 'day', 'hour'] as PayFrequency[]).map((period) => (
                <button
                  key={period}
                  onClick={() => setSelectedPeriod(period)}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-lg transition-all capitalize whitespace-nowrap ${
                    selectedPeriod === period
                      ? 'bg-emerald-500 text-zinc-950 font-bold shadow'
                      : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
                  }`}
                >
                  {period === '4week' ? '4-Wk' : period === '2week' ? 'Fortnight' : period}
                </button>
              ))}
            </div>
          </div>

          {/* Large Primary Net Pay Number */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 py-2">
            <div>
              <div className="text-xs text-emerald-400 font-bold uppercase tracking-wider mb-1">
                {activePeriod.label}
              </div>
              <div className="text-4xl sm:text-5xl font-black tracking-tight text-white font-mono">
                {formatGBP(activePeriod.data.netPay)}
              </div>
            </div>

            <div className="flex items-center gap-2 self-start sm:self-auto">
              <button
                onClick={handleCopySummary}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-xl bg-zinc-950 hover:bg-zinc-800 text-zinc-200 border border-zinc-800 transition-colors"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <FileText className="w-3.5 h-3.5 text-emerald-400" />}
                <span>{copied ? 'Copied!' : 'Copy Summary'}</span>
              </button>
            </div>
          </div>

          {/* Progress Bar: Net vs Tax & Deductions */}
          <div className="space-y-1.5 pt-2">
            <div className="flex justify-between text-xs text-zinc-300 font-medium">
              <span className="flex items-center gap-1 text-emerald-400 font-bold">
                Net Pay: {((activePeriod.data.netPay / activePeriod.data.gross) * 100 || 0).toFixed(1)}%
              </span>
              <span className="text-zinc-400 font-mono">
                Deductions: {((activePeriod.data.totalDeductions / activePeriod.data.gross) * 100 || 0).toFixed(1)}%
              </span>
            </div>
            <div className="h-3 w-full bg-zinc-950 rounded-full overflow-hidden flex border border-zinc-800">
              <div
                style={{ width: `${Math.min(100, Math.max(0, (activePeriod.data.netPay / activePeriod.data.gross) * 100))}%` }}
                className="bg-emerald-500 h-full transition-all duration-500"
                title="Net Take Home Pay"
              />
              <div
                style={{ width: `${Math.min(100, Math.max(0, (activePeriod.data.incomeTax / activePeriod.data.gross) * 100))}%` }}
                className="bg-rose-500 h-full transition-all duration-500"
                title="Income Tax"
              />
              <div
                style={{ width: `${Math.min(100, Math.max(0, (activePeriod.data.nationalInsurance / activePeriod.data.gross) * 100))}%` }}
                className="bg-amber-500 h-full transition-all duration-500"
                title="National Insurance"
              />
              <div
                style={{ width: `${Math.min(100, Math.max(0, (activePeriod.data.pension / activePeriod.data.gross) * 100))}%` }}
                className="bg-purple-500 h-full transition-all duration-500"
                title="Pension"
              />
              <div
                style={{ width: `${Math.min(100, Math.max(0, (activePeriod.data.studentLoan / activePeriod.data.gross) * 100))}%` }}
                className="bg-sky-500 h-full transition-all duration-500"
                title="Student Loan"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Grid of Key Financial Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
        {/* Gross Income Card */}
        <div className="bg-zinc-900 p-4 rounded-xl border border-zinc-800 shadow-md space-y-1">
          <div className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider">Gross Income</div>
          <div className="text-xl font-extrabold text-white font-mono">
            {formatGBP(activePeriod.data.gross)}
          </div>
          <div className="text-[11px] text-zinc-500 font-mono">{formatGBP(result.annualGross)} / yr</div>
        </div>

        {/* Income Tax Card */}
        <div className="bg-zinc-900 p-4 rounded-xl border border-zinc-800 border-l-4 border-l-rose-500 shadow-md space-y-1">
          <div className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider">Income Tax</div>
          <div className="text-xl font-extrabold text-rose-400 font-mono">
            -{formatGBP(activePeriod.data.incomeTax)}
          </div>
          <div className="text-[11px] text-zinc-500 font-mono">{formatGBP(result.incomeTax)} / yr</div>
        </div>

        {/* National Insurance Card */}
        <div className="bg-zinc-900 p-4 rounded-xl border border-zinc-800 border-l-4 border-l-amber-500 shadow-md space-y-1">
          <div className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider">National Insurance</div>
          <div className="text-xl font-extrabold text-amber-400 font-mono">
            -{formatGBP(activePeriod.data.nationalInsurance)}
          </div>
          <div className="text-[11px] text-zinc-500 font-mono">{formatGBP(result.nationalInsurance)} / yr</div>
        </div>

        {/* Total Deductions Card */}
        <div className="bg-zinc-900 p-4 rounded-xl border border-zinc-800 border-l-4 border-l-zinc-600 shadow-md space-y-1">
          <div className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider">Total Deductions</div>
          <div className="text-xl font-extrabold text-zinc-200 font-mono">
            -{formatGBP(activePeriod.data.totalDeductions)}
          </div>
          <div className="text-[11px] text-zinc-500 font-mono">{formatGBP(result.totalDeductions)} / yr</div>
        </div>
      </div>

      {/* Pension & Student Loan (if applicable) */}
      {(result.pensionContribution > 0 || result.studentLoanDeduction > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          {result.pensionContribution > 0 && (
            <div className="bg-purple-950/40 border border-purple-500/30 p-3.5 rounded-xl flex items-center justify-between">
              <div>
                <span className="text-xs font-bold text-purple-300 block">Pension Contribution</span>
                <span className="text-[11px] text-purple-400">
                  Method: {result.inputs.pensionMethod.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="text-right font-mono font-bold text-purple-300 text-lg">
                -{formatGBP(activePeriod.data.pension)}
                <span className="block text-[11px] font-normal text-purple-400">{formatGBP(result.pensionContribution)} / yr</span>
              </div>
            </div>
          )}

          {result.studentLoanDeduction > 0 && (
            <div className="bg-sky-950/40 border border-sky-500/30 p-3.5 rounded-xl flex items-center justify-between">
              <div>
                <span className="text-xs font-bold text-sky-300 block">Student Loan Repayment</span>
                <span className="text-[11px] text-sky-400 uppercase">
                  {result.inputs.studentLoanPlan.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="text-right font-mono font-bold text-sky-300 text-lg">
                -{formatGBP(activePeriod.data.studentLoan)}
                <span className="block text-[11px] font-normal text-sky-400">{formatGBP(result.studentLoanDeduction)} / yr</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* In-content ad — sits right where users are looking at their results */}
      <AdSlot slot="in-article" className="w-full" />

      {/* Tax Rates Summary Callout */}
      <div className="bg-zinc-900 rounded-xl p-3.5 border border-zinc-800 flex flex-wrap items-center justify-between gap-3 text-xs text-zinc-300">
        <div className="flex items-center gap-2">
          <Percent className="w-4 h-4 text-emerald-400" />
          <span>
            Effective Tax Rate:{' '}
            <strong className="text-white font-mono font-bold">{result.effectiveTaxRate.toFixed(1)}%</strong>
          </span>
        </div>
        <div>
          <span>
            Marginal Rate:{' '}
            <strong className="text-white font-mono font-bold">{result.marginalTaxRate}%</strong>
          </span>
        </div>
        <div className="text-zinc-400 font-medium">
          Tax-Free Allowance: <strong className="text-emerald-400 font-mono">£{result.personalAllowance.toLocaleString()}</strong>
        </div>
      </div>
    </div>
  );
};

